/// <reference path="../../../app.definition.ts" />
import * as angular from "angular";
import { Lightbox } from "angular-bootstrap-lightbox";
import { JavaApiAdapterService } from "@common/apiadapter/javaapi.service";
import { Subject } from "@common/model/subject";
import { SubjectImage } from "@common/model/subjectImage";
import { User } from "@common/model/user";
import { PopUpService } from "@common/popup/popup.service";
import { BroadcastService } from "@common/service/broadcast.service";
import { CacheService } from "@common/service/cache.service";
import { CommonService } from "@common/common.service";
import { PaymentService } from "@common/service/payment.service";
import { TeamVaultService } from "@common/service/teamvault.service";
import { IAppRootScopeService } from "@/app/app.definition";

export interface IEcvImageGallery {
  images: Array<any>; // main array used for displaying images in the grid
  inline: boolean;
  limit: number;
  mode: string;
  addButton: boolean;
  edit: boolean;
  imageFiles: Array<any>; // array containing the image file objects until they are saved
  galleryLoaded: boolean;
  subject: Subject;
  canAddNew: boolean;
  init(): void;
  openGallery(mode: string): void;
  deleteImage(index: number): void;
  viewImage(index: number): void;
  addImage($file, $errFile): void;
  openPhotoLimitPopup(): void;
}

class EcvImageGallery implements IEcvImageGallery {
  images: Array<any>;
  inline: boolean;
  limit: number;
  currentOpenModal: any;
  mode: string;
  addButton: boolean;
  subject: Subject;
  user: User;
  edit: boolean;
  imageFiles: Array<any>;
  imagesCopy: Array<any>;
  galleryLoaded: boolean;
  canAddNew: boolean;
  static $inject = ["$uibModal", "CacheService", "Lightbox", "$rootScope",
    "TeamVaultService", "CommonService", "PopUpService", "ApiAdapterService", "PaymentService", "BroadcastService"];
  constructor(
    private $uibModal: angular.ui.bootstrap.IModalService,
    private $_cache: CacheService,
    private Lightbox: Lightbox,
    private $rootScope: IAppRootScopeService,
    private $_team: TeamVaultService,
    private CommonService: CommonService,
    private PopUpService: PopUpService,
    private ApiAdapterService: JavaApiAdapterService,
    private PaymentService: PaymentService,
    private BroadcastService: BroadcastService
  ) {
    // initialize the value of the edit flag
    if (!!this.edit) {
      // do something
    } else {
      this.edit = false;
    }
  }

  init(): void {
    this.user = this.$_cache.getUser();
    if (!!this.subject) {
      // do something
    } else {
      this.subject = this.$_cache.getCurrentSubject();
    }

    // add modify flag to all the images if the logged in user is the owner of the team
    if (this.user.id == this.subject.ownerId && !!this.images) {
      for (let index = 0; index < this.images.length; index++) {
        this.images[index]['modify'] = true;
      }
    } else if (!!this.images) {
      // add modify flag only to images uploaded by the user if the user is a TM
      for (let index = 0; index < this.images.length; index++) {
        if (this.images[index].addedBy == this.user.id) {
          this.images[index]['modify'] = true;
        }
      }
    }

    // making a copy to be passed to the gallery popup
    this.imagesCopy = angular.copy(this.images);

    // flags to indicate that the gallery data has been loaded - used for spinners
    this.galleryLoaded = true;
    this.$rootScope.imageUploading = false;

    // initialize the imageFiles array - it will store the file objects
    this.imageFiles = [];

    // initialize the limit - number of images shown on the cr-profile
    this.initializeLimit();
  }


  // this function initializes the number of images shown on the cr-profile
  initializeLimit(): void {
    // operations for calculating the limit dynamically, can be performed here
    // currently using a static number
    this.limit = 5;
  }

  // this function opens the gallery popup to display image grid
  openGallery(mode: string): void {
    this.PaymentService.checkLimitAndProceed('photo', null, true).then((s) => {
      this.openGalleryModal(mode, true);
    }, (e) => {
      this.openGalleryModal(mode, false);
    });
  }

  // this function opens the gallery modal
  openGalleryModal(mode: string, allowNew: boolean): void {
    this.imagesCopy = angular.copy(this.images);
    this.mode = mode;
    const context = this;
    this.$rootScope.imageUploading = false;
    this.currentOpenModal = this.$uibModal.open({
      templateUrl: 'app/common/popup/galleryPopup/galleryPopup.html',
      controller: 'GalleryPopupController',
      controllerAs: 'galleryPopupVm',
      resolve: {
        images: () => this.imagesCopy || null,
        mode: () => mode,
        subject: () => context.subject,
        allowNew: () => allowNew
      }
    }).result.then(
      function (s) {
        context.saveImages(s);
      },
      function (e) {
        context.saveImages(context.imagesCopy);
      }
    );
  }

  // this function deletes an image from the gallery
  deleteImage(index: number): void {
    const selectedImage = this.images.filter(function (image) {
      return image.id == index;
    });
    // set the delete flag to indicate that the image has been deleted
    this.images[this.images.indexOf(selectedImage[0])]['deleted'] = true;
  }


  // this function opens a modal to view a particular image
  viewImage(index: number): void {
    const lightboxImages = this.images.filter(function (image) {
      return image.deleted != true;
    });

    const selectedImage = lightboxImages.filter(function (image) {
      return image.id == index;
    });

    this.Lightbox.openModal(lightboxImages, lightboxImages.indexOf(selectedImage[0]));
  }

  // this function adds an image to the gallery
  addImage($file, $errFile) {
    this.$rootScope.mainShowLoader = true;

    if (!$file) {
      this.$rootScope.mainShowLoader = false;
      return;
    }

    $file.filename = $file.name;
    if (!this.$_team.checkDocument($file)) {
      this.$rootScope.mainShowLoader = false;
      return;
    }

    if ($file.filename.length > 99) {
      const ext = this.CommonService.getExtension($file.filename.toLowerCase());
      $file.filename = $file.filename.substr(0, 100 - (ext.length + 1)) + '.' + ext;
    }

    if (!this.$_team.checkFile($file)) {
      this.PopUpService.openInformationModal('File has a size greater than 7MB.');
      this.$rootScope.mainShowLoader = false;
      return;
    }

    const ext = this.CommonService.getExtension($file.filename.toLowerCase());

    // push the file object to the imageFiles array to display thumbnail while file is uploaded
    this.imageFiles.push($file);

    // store the length of the file array - used to toggle the loading spinner
    this.$rootScope.imageFilesLength = this.imageFiles.length;

    // upload the image
    this.uploadImage($file);

    this.$rootScope.mainShowLoader = false;
  }


  // this function uploads the image by making an api call
  uploadImage(file: any): void {
    const context = this;
    const tempSubjectImage = new SubjectImage();
    tempSubjectImage.deleted = false;
    tempSubjectImage.sortOrder = this.images.length + 1;
    tempSubjectImage.isNewlyAdded = true;

    const reader = new FileReader(); // Reading in Base64
    reader.onload = function (e) {
      let content;
      if (!e) {
        content = reader['content'];
      }
      else {
        content = e.target['result'];
      }
      content = window.btoa(content);
      tempSubjectImage.photo = content;

      // save the image content and get the image object from the backend
      context.ApiAdapterService.addSubjectImage(context.subject.subjectId, tempSubjectImage).then((s) => {
        const resp = s;

        // set the modify & new flags to true - for the delete icon on newly added images
        s['modify'] = true;
        s['new'] = true;

        // push the image to the main image array
        context.images.push(s);
        context.imagesCopy.push(s);

        // remove the image from the imageFiles array
        context.imageFiles.splice(context.imageFiles.indexOf(file), 1);
        context.$rootScope.imageFilesLength = context.imageFiles.length;

        // if the image is uploaded after the modal is closed, refresh the gallery in cr-profile
        if (context.imageFiles.length == 0) {
          if (!!context.$rootScope.imageUploading && context.$rootScope.imageUploading == true) {
            context.BroadcastService.broadcast('update-gallery');
          }
        }
      }, (e) => {
        context.imageFiles.splice(context.imageFiles.indexOf(file), 1);
        context.$rootScope.imageFilesLength = context.imageFiles.length;
        if (e == "com.ecarevault.exception.LimitExceededException") {
          context.canAddNew = false;
          context.openPhotoLimitPopup();
        }
        console.log(e);
      });
    }

    const blob = file.slice();
    reader['readAsBinaryString'](blob);
  }


  // this function saves the add/delete modifications to the gallery
  saveImages(imageList: Array<any>): void {

    // set the flag to indicate that the modal is closed but the image is still being uploaded
    if (this.$rootScope.imageFilesLength > 0) {
      this.$rootScope.imageUploading = true;
      this.$rootScope.imageFilesLength = 0;
    }

    // a flag to indicate whether any image is added/deleted from the gallery
    // to prevent an api call if no changes are made
    let modified = false;

    // filter the images which are newly added or deleted
    const tempList = imageList.filter(function (image) {
      return (image.deleted == true || image.new == true);
    });

    modified = tempList.length > 0;
    if (modified == true) {
      let order = 0;

      // format the image data as needed by the api
      imageList = imageList.map(function (image) {
        // update the sortOrder based on the current position in the array
        return {
          "id": image.id,
          "deleted": image.deleted,
          "sortOrder": (image.deleted == true ? 0 : ++order),
          "isNewlyAdded": image.isNewlyAdded
        };
      });
      this.galleryLoaded = false;

      // api call to save the image objects
      this.ApiAdapterService.updateSubjectImages(this.subject.subjectId, imageList).then((s) => {

        // reload the image grid in the cr-profile
        this.images = angular.copy(s);
        this.imagesCopy = angular.copy(s);
        this.galleryLoaded = true;
      }, (e) => {
        this.galleryLoaded = true;
        console.log(e);
      });
    }
  }

  openPhotoLimitPopup(): void {
    const popupTitle = this.PaymentService.getPopupTitle("photo");
    const popupMessage = this.PaymentService.getPopupMessage("photo");
    this.PopUpService.openAccountLimitAlertPopup(popupTitle, popupMessage).then((s) => {
      this.BroadcastService.broadcast('close-gallery');
    }, (e) => {
      console.log(e);
    });
  }
}

function imageGallery(): ng.IDirective {
  return {
    restrict: 'E',
    templateUrl: 'app/common/directive/image-gallery/templates/image-gallery.directive.html',
    replace: true,
    controller: EcvImageGallery,
    controllerAs: 'galleryVm',
    scope: true,
    bindToController: {
      'images': '=?ecvImages',
      'inline': '=?ecvInline',
      'addButton': '=?ecvAddButton',
      'edit': '=?ecvEdit',
      'subject': '=?ecvSubject',
      'canAddNew': '=?ecvCanAdd'
    },
    link: (scope: ng.IScope, element: ng.IAugmentedJQuery, attributes: ng.IAttributes, controller: EcvImageGallery): void => {
      controller.init();
    }
  }
}

angular.module('eCareVault.common.directives').directive('ecvImageGallery', imageGallery);
